import type {
  ApiError,
  Drawing,
  MatchView,
  Me,
  Reference,
  Replay,
  RoundDetail,
} from "./types";

/**
 * Where a replay is read from.
 *
 * Its owner reads it by id, signed in. Anybody else reads it through its share
 * link, which needs no account and only ever reads: the same endpoints are
 * mounted under both, so everything that plays a replay takes a base path.
 */
export type ReplaySource = { kind: "owned"; id: string } | { kind: "shared"; token: string };

export function replayBase(source: ReplaySource): string {
  return source.kind === "owned"
    ? `/api/replays/${encodeURIComponent(source.id)}`
    : `/api/shared/${encodeURIComponent(source.token)}`;
}

/** Where signing in starts, coming back to `next` afterwards. */
export function loginURL(next = "/"): string {
  return `/api/auth/login?next=${encodeURIComponent(next)}`;
}

/** Raised for any non-2xx response, carrying the API's machine-readable code. */
export class RequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  if (!response.ok) {
    let body: ApiError | undefined;
    try {
      body = (await response.json()) as ApiError;
    } catch {
      // fall through to a generic message
    }
    throw new RequestError(
      body?.code ?? "unknown",
      body?.message ?? `HTTP ${response.status}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/** A request whose success is the only answer: 204 and no body. */
async function send(path: string, method = "DELETE", body?: unknown): Promise<void> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw new RequestError("request_failed", `HTTP ${response.status}`, response.status);
  }
}

export const api = {
  reference: () => request<Reference>("/api/reference"),

  health: () => request<{ status: string; debug?: boolean }>("/api/health"),

  me: () => request<Me>("/api/auth/me"),

  logout: () => send("/api/auth/logout", "POST"),

  /** Deletes the account and every replay it uploaded. */
  deleteAccount: () => send("/api/account"),

  /** The replay's share link, made the first time it is asked for. */
  share: (id: string) =>
    request<{ shareToken: string }>(`/api/replays/${id}/share`, { method: "POST" }).then(
      (r) => r.shareToken,
    ),

  /** Builds the simulated match used to check the viewer against every agent. */
  buildSimulation: () =>
    request<Replay>("/api/debug/simulation", { method: "POST" }),

  listReplays: () =>
    request<{ replays: Replay[] }>("/api/replays").then((r) => r.replays),

  getReplay: (id: string) => request<Replay>(`/api/replays/${id}`),

  deleteReplay: (id: string) =>
    fetch(`/api/replays/${id}`, { method: "DELETE" }).then((r) => {
      if (!r.ok) throw new RequestError("delete_failed", "suppression impossible", r.status);
    }),

  getMatch: (base: string) => request<MatchView>(`${base}/match`),

  listDrawings: (base: string) =>
    request<{ drawings: Drawing[] }>(`${base}/drawings`).then((r) => r.drawings),

  addDrawing: (id: string, drawing: Omit<Drawing, "id">) =>
    request<Drawing>(`/api/replays/${id}/drawings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(drawing),
    }),

  moveDrawing: (id: string, drawing: number, points: Array<[number, number]>) =>
    send(`/api/replays/${id}/drawings/${drawing}`, "PATCH", { points }),

  deleteDrawing: (id: string, drawing: number) =>
    send(`/api/replays/${id}/drawings/${drawing}`),

  clearDrawings: (id: string, round: number) =>
    send(`/api/replays/${id}/drawings?round=${round}`),

  getRound: (base: string, round: number) => request<RoundDetail>(`${base}/rounds/${round}`),

  getTracks: async (base: string, round: number): Promise<ArrayBuffer> => {
    const response = await fetch(`${base}/rounds/${round}/tracks`);
    if (!response.ok) {
      throw new RequestError("tracks_failed", "positions indisponibles", response.status);
    }
    return response.arrayBuffer();
  },

  /** Uploads a replay, reporting progress. XMLHttpRequest is used because fetch
   *  still cannot report upload progress. */
  upload(file: File, onProgress?: (fraction: number) => void): Promise<Replay> {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/replays");
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress?.(e.loaded / e.total);
      });
      xhr.addEventListener("load", () => {
        let body: unknown;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          reject(new RequestError("unknown", "réponse illisible du serveur", xhr.status));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body as Replay);
        } else {
          const err = body as ApiError;
          reject(new RequestError(err.code ?? "unknown", err.message ?? "échec", xhr.status));
        }
      });
      xhr.addEventListener("error", () =>
        reject(new RequestError("network", "connexion interrompue", 0)),
      );
      xhr.send(form);
    });
  },

  /** Follows decoding progress until the replay reaches a terminal state. */
  watch(id: string, onUpdate: (replay: Replay) => void): () => void {
    const source = new EventSource(`/api/replays/${id}/events`);
    source.onmessage = (event) => {
      const replay = JSON.parse(event.data) as Replay;
      onUpdate(replay);
      if (["ready", "failed", "unsupported", "expired"].includes(replay.status)) {
        source.close();
      }
    };
    source.onerror = () => source.close();
    return () => source.close();
  },
};
